import { useEffect, useRef, useState } from 'react';
import type { WeeklyCopyFormat } from '../../../../lib/shared/types';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import { Dialog } from '../ui/Dialog';
import { LoadingStatus } from '../ui/LoadingFeedback';
import { useWeeklyCopyPreference } from './useWeeklyCopyPreference';

function SettingsContent({ onClose }: { onClose: () => void }) {
  const { t } = useI18n();
  const settings = useWeeklyCopyPreference();
  const [draft, setDraft] = useState<WeeklyCopyFormat>();
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  return (
    <Dialog
      isOpen
      isCloseDisabled={saving}
      onClose={() => {
        if (!saving) onClose();
      }}
      title={t('weeklyCopy.settings')}
      className="max-w-xl"
    >
      <div className="overflow-y-auto p-5 sm:p-6">
        {settings.loading ? (
          <LoadingStatus label={t('message.loading')} />
        ) : settings.error ? (
          <div role="alert" className="space-y-3">
            <p>{t('weeklyCopy.loadFailed')}</p>
            <button type="button" className="btn-secondary min-h-11" onClick={settings.retry}>
              {t('message.retry')}
            </button>
          </div>
        ) : (
          <form
            className="space-y-5"
            onSubmit={async event => {
              event.preventDefault();
              if (saving || !settings.format) return;
              setSaving(true);
              setFailed(false);
              const saved = await settings.save(draft ?? settings.format);
              if (!active.current) return;
              if (saved) onClose();
              else setFailed(true);
              setSaving(false);
            }}
          >
            <fieldset disabled={saving} className="space-y-2">
              <legend className="mb-2 text-sm font-medium">{t('weeklyCopy.format')}</legend>
              {(['rtf', 'markdown', 'whatsapp'] as const).map(format => (
                <label key={format} className="flex min-h-11 items-center gap-3 text-sm">
                  <input
                    type="radio"
                    name="copy-format"
                    value={format}
                    checked={(draft ?? settings.format) === format}
                    onChange={() => setDraft(format)}
                  />
                  {t(`weeklyCopy.${format}`)}
                </label>
              ))}
            </fieldset>
            <p className="text-sm text-gray-600">{t('weeklyCopy.formatHelp')}</p>
            <p className="text-sm text-gray-600">{t('weeklyCopy.richHelp')}</p>
            {failed && (
              <p role="alert" className="text-sm text-red-700">
                {t('weeklyCopy.saveFailed')}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                disabled={saving}
                className="btn-secondary min-h-11"
                onClick={onClose}
              >
                {t('message.cancel')}
              </button>
              <button type="submit" disabled={saving} className="btn-primary min-h-11">
                {t(saving ? 'message.saving' : 'message.save')}
              </button>
            </div>
          </form>
        )}
      </div>
    </Dialog>
  );
}

export function WeeklyCopySettings({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  return user ? <SettingsContent key={user.id} onClose={onClose} /> : null;
}
